import React, { useEffect, useState, useMemo } from "react";
import "../../styles/ProductDetails.css";
import "react-image-gallery/styles/css/image-gallery.css";
import ImageGallery from "react-image-gallery";
import {
  RemoveRounded,
  AddRounded,
  LocalMallOutlined,
  ShoppingCartOutlined,
} from "@mui/icons-material";
import { useParams, useLocation } from "react-router-dom";
import { usePreserveStoreIdNavigate } from "../utils/usePreserveStoreIdNavigate";
import { useGetInventoryPackDetailsQuery } from "../../store/apiSlice";
import Toaster from "../utils/Toaster";
import Loader from "../utils/Loader";
import Modal from "../utils/Modal";
import styled from "@emotion/styled";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Container,
  Stack,
  Divider,
  Alert,
  IconButton,
  Paper,
  Tooltip,
} from "@mui/material";
import { useTheme } from "../../theme/ThemeContext";
import { addToCart } from "../../utils/cart";
import { AppTypography } from "../common/Typography";
import { getPackDisplayImage, getPackDisplayImages } from "../../utils/images";
import { useStoreInfo } from "../../context/StoreInfoContext";

const SizeButton = styled(Button)(({ theme, selected, disabled }) => ({
  minWidth: 50,
  height: 40,
  borderRadius: 20,
  fontWeight: 600,
  fontSize: "0.875rem",
  border: `2px solid ${theme?.app_button_color}`,
  color: selected ? "white" : theme?.app_button_color,
  background: selected ? theme?.app_button_color : "white",
  opacity: disabled ? 0.5 : 1,
  cursor: disabled ? "not-allowed" : "pointer",
  transition: "all 0.2s ease",
  "&:hover": {
    borderColor: theme?.app_button_color,
    background: theme?.app_button_color,
    color: "white",
    transform: "translateY(-1px)",
  },
}));

// Utility to check if a string contains HTML tags
function isHTML(str) {
  return /<([a-z][\s\S]*?)>/i.test(str);
}

const variantSwatch = (variant) => {
  const hex = String(variant?.hex_code ?? "").trim();
  if (!hex) return null;
  if (/^[0-9a-f]{3}([0-9a-f]{3})?$/i.test(hex)) return `#${hex}`;
  return hex;
};

function ColorChoice({ variant, isSelected, isAvailable, theme }) {
  const hex = variantSwatch(variant);
  const label = String(variant?.color ?? "").trim() || "Color";

  if (!hex) {
    return (
      <Box
        sx={{
          px: 1.25,
          minHeight: 30,
          borderRadius: "16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "0.75rem",
          fontWeight: 600,
          backgroundColor: isSelected ? theme?.app_button_color : "white",
          color: isSelected ? "white" : theme?.app_text,
          border: `1px solid ${isSelected ? theme?.app_button_color : isAvailable ? "#E0E0E0" : "#CCCCCC"}`,
        }}
      >
        {label}
      </Box>
    );
  }

  const isLight =
    hex.toLowerCase() === "#ffffff" ||
    hex.toLowerCase() === "#fff" ||
    hex.toLowerCase() === "white";

  return (
    <Box
      sx={{
        width: 30,
        height: 30,
        borderRadius: "50%",
        backgroundColor: hex,
        border: isSelected
          ? `1px solid ${theme?.app_button_color}`
          : `2px solid ${isAvailable ? "#E0E0E0" : "#CCCCCC"}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "all 0.2s ease",
        position: "relative",
        "&:hover": isAvailable
          ? {
            transform: "scale(1.1)",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
            borderColor: theme?.app_button_color,
          }
          : {},
        "&::after": isSelected
          ? {
            content: '"✓"',
            position: "absolute",
            color: isLight ? "#333" : "white",
            fontSize: "12px",
            fontWeight: "bold",
            textShadow: isLight
              ? "0 1px 2px rgba(255,255,255,0.8)"
              : "0 1px 2px rgba(0,0,0,0.3)",
          }
          : {},
      }}
    />
  );
}

const ProductDetails = () => {
  const theme = useTheme();
  const { id } = useParams();
  const navigate = usePreserveStoreIdNavigate();
  const location = useLocation();
  const { customerId, storeId } = useStoreInfo();
  const { storeInfo } = useStoreInfo();

  const searchParams = new URLSearchParams(location.search);
  const fetchType = searchParams.get("type") || "pack";

  // Use the new API to fetch pack details by ID
  const {
    data: packData,
    isLoading,
    error,
  } = useGetInventoryPackDetailsQuery({
    customerId,
    storeId,
    packId: id,
    type: fetchType,
  });

  // Get the pack from the API response
  const pack = packData?.result;

  // Use the first product in the pack for images and variants, or fallback to pack itself
  const product = pack?.products?.[0] || pack;

  // Unlimited only when API sends false or 0; 1, true, or undefined → limited (default)
  const isLimitedStock =
    fetchType !== "product" ||
    (pack?.limited_stock !== false && pack?.limited_stock !== 0);

  // Product with unlimited stock: qty 0 should not block selection or show out of stock
  const isUnlimitedProduct = fetchType === "product" && !isLimitedStock;

  // Helper function to check if a variant is onesize (case-insensitive)
  const isOnesize = (variant) => {
    const size = variant?.size?.toLowerCase() || "";
    return size === "onesize" || size === "one size" || size === "one-size";
  };

  // Variants: if product has variants, use them, else empty array
  const variants = useMemo(() => product?.variants || [], [product?.variants]);

  // Filter out onesize variants for display (but keep all variants for selection logic)
  const displayableVariants = useMemo(
    () => variants.filter((v) => !isOnesize(v)),
    [variants],
  );

  // Only show variant selection if there are non-onesize variants to display
  // OR if there are multiple variants (even if some are onesize)
  const hasVariants = useMemo(() => {
    // If only one variant exists, don't show it (will be auto-selected)
    if (variants.length === 1) return false;
    // If all variants are onesize, don't show them (will be auto-selected)
    if (variants.length > 0 && displayableVariants.length === 0) return false;
    // Show if there are multiple variants and at least one is not onesize
    return displayableVariants.length > 0;
  }, [variants, displayableVariants]);

  // State for selected variant and quantity
  const [selectedVariant, setSelectedVariant] = useState(null);
  const selectedCatalogSku =
    selectedVariant?.variant_sku ||
    selectedVariant?.client_sku ||
    selectedVariant?.sku ||
    "";
  const imageUrls = useMemo(
    () => (pack ? getPackDisplayImages(pack, undefined, selectedCatalogSku) : []),
    [pack, selectedCatalogSku],
  );
  const images = useMemo(
    () =>
      imageUrls.length > 0
        ? imageUrls.map((url) => ({ original: url, thumbnail: url }))
        : [
          {
            original: getPackDisplayImage(pack, undefined, selectedCatalogSku),
            thumbnail: getPackDisplayImage(pack, undefined, selectedCatalogSku),
          },
        ],
    [imageUrls, pack, selectedCatalogSku],
  );
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [showSizeChart, setShowSizeChart] = useState(false);
  const [showQuantityAlert, setShowQuantityAlert] = useState(false);
  const [maxQuantity, setMaxQuantity] = useState(null);
  const [added, setAdded] = useState(false);

  // Group variants by size
  const variantsBySize = useMemo(() => {
    const grouped = {};
    displayableVariants.forEach((variant) => {
      const size = variant.size?.toLowerCase() || "default";
      if (!grouped[size]) {
        grouped[size] = [];
      }
      grouped[size].push(variant);
    });
    return grouped;
  }, [displayableVariants]);

  // Get unique sizes
  const uniqueSizes = useMemo(() => {
    return Object.keys(variantsBySize);
  }, [variantsBySize]);

  // Get colors for selected size
  const colorsForSelectedSize = useMemo(() => {
    if (!selectedSize) return [];
    return variantsBySize[selectedSize] || [];
  }, [selectedSize, variantsBySize]);

  // Only show color picker when at least one variant has a color (size-only products work without color)
  const hasColorVariants = useMemo(
    () =>
      colorsForSelectedSize.some(
        (v) => v.color != null && String(v.color).trim() !== "",
      ),
    [colorsForSelectedSize],
  );

  // Onesize with multiple colors: all variants are onesize but have different colors (e.g. Red, Blue)
  const hasOnesizeColorVariants = useMemo(
    () =>
      variants.length > 1 &&
      displayableVariants.length === 0 &&
      variants.some((v) => v.color != null && String(v.color).trim() !== ""),
    [variants, displayableVariants],
  );

  // For onesize+multi-color: use all variants as the color source (no size filter)
  const colorsForColorPicker = useMemo(
    () => (hasOnesizeColorVariants ? variants : colorsForSelectedSize),
    [hasOnesizeColorVariants, variants, colorsForSelectedSize],
  );

  const hasColorPickerVariants = useMemo(
    () =>
      colorsForColorPicker.some(
        (v) => v.color != null && String(v.color).trim() !== "",
      ),
    [colorsForColorPicker],
  );

  // When variants change, reset selection and quantity
  useEffect(() => {
    // Auto-select if:
    // 1. Only one variant exists (regardless of size)
    // 2. All variants are onesize
    if (variants && variants.length === 1) {
      // Auto-select the only variant
      setSelectedVariant(variants[0]);
      setQuantity(1);
      setShowQuantityAlert(
        variants[0].quantity > 0 && variants[0].quantity <= 5,
      );
      setMaxQuantity(
        variants[0].quantity > 0 && variants[0].quantity <= 5
          ? variants[0].quantity
          : null,
      );
    } else if (
      variants &&
      variants.length > 0 &&
      displayableVariants.length === 0
    ) {
      // All variants are onesize - auto-select the first one
      const firstOnesize = variants.find((v) => isOnesize(v)) || variants[0];
      setSelectedVariant(firstOnesize);
      const hasColor =
        firstOnesize.color != null && String(firstOnesize.color).trim() !== "";
      setSelectedColor(
        hasColor ? firstOnesize.color : `variant-${firstOnesize.id}`,
      );
      setQuantity(1);
      setShowQuantityAlert(
        firstOnesize.quantity > 0 && firstOnesize.quantity <= 5,
      );
      setMaxQuantity(
        firstOnesize.quantity > 0 && firstOnesize.quantity <= 5
          ? firstOnesize.quantity
          : null,
      );
    } else {
      setSelectedVariant(null);
      setSelectedSize(null);
      setSelectedColor(null);
      setQuantity(1);
      setShowQuantityAlert(false);
      setMaxQuantity(null);
    }
  }, [variants, displayableVariants]);

  const handleSizeSelect = (size) => {
    setSelectedSize(size);
    setSelectedColor(null);
    setSelectedVariant(null);
    setQuantity(1);
    setShowQuantityAlert(false);
    setMaxQuantity(null);
  };

  // Auto-select first available color when size is selected
  useEffect(() => {
    if (selectedSize && !selectedColor && colorsForSelectedSize.length > 0) {
      const availableColors = isUnlimitedProduct
        ? colorsForSelectedSize
        : colorsForSelectedSize.filter((v) => v.quantity > 0);
      if (availableColors.length > 0) {
        const variant = availableColors[0];
        const hasColor =
          variant.color != null && String(variant.color).trim() !== "";
        const selectionKey = hasColor ? variant.color : `variant-${variant.id}`;
        setSelectedColor(selectionKey);
        setSelectedVariant(variant);
        setQuantity(1);
        setShowQuantityAlert(variant.quantity > 0 && variant.quantity <= 5);
        setMaxQuantity(
          variant.quantity > 0 && variant.quantity <= 5
            ? variant.quantity
            : null,
        );
      }
    }
  }, [selectedSize, selectedColor, colorsForSelectedSize, isUnlimitedProduct]);

  const handleColorSelect = (colorOrKey) => {
    // For onesize+multi-color: no size selection needed, use colorsForColorPicker
    const sourceVariants = hasOnesizeColorVariants
      ? colorsForColorPicker
      : colorsForSelectedSize;
    if (!hasOnesizeColorVariants && !selectedSize) return;

    // Find the variant: by color match or by null-color key (variant-${id})
    const variant = sourceVariants.find((v) => {
      if (colorOrKey?.startsWith?.("variant-")) {
        return (
          v.id != null && String(v.id) === colorOrKey.replace(/^variant-/, "")
        );
      }
      return (
        v.color != null && v.color?.toLowerCase() === colorOrKey?.toLowerCase()
      );
    });

    if (variant) {
      setSelectedColor(colorOrKey);
      setSelectedVariant(variant);
      setQuantity(1);
      setShowQuantityAlert(variant.quantity > 0 && variant.quantity <= 5);
      setMaxQuantity(
        variant.quantity > 0 && variant.quantity <= 5 ? variant.quantity : null,
      );
    }
  };

  const handleVariantSelect = (variant) => {
    setSelectedVariant(variant);
    setQuantity(1);
  };

  const handleQuantityChange = (delta) => {
    if (!selectedVariant) {
      // If there's only one variant or all are onesize, auto-select it
      if (variants && variants.length === 1) {
        setSelectedVariant(variants[0]);
        const maxQ = isLimitedStock ? variants[0].quantity || 1 : 99999;
        const newQuantity = Math.max(1, Math.min(quantity + delta, maxQ));
        setQuantity(newQuantity);
        return;
      } else if (
        variants &&
        variants.length > 0 &&
        displayableVariants.length === 0
      ) {
        // All variants are onesize - auto-select the first one
        const firstOnesize = variants.find((v) => isOnesize(v)) || variants[0];
        setSelectedVariant(firstOnesize);
        const maxQ = isLimitedStock ? firstOnesize.quantity || 1 : 99999;
        const newQuantity = Math.max(1, Math.min(quantity + delta, maxQ));
        setQuantity(newQuantity);
        return;
      } else {
        // Check if size and color selection is required
        if (!selectedSize) {
          Toaster.show("Select a size", "info");
          return;
        }
        if (colorsForSelectedSize.length > 0 && !selectedColor) {
          Toaster.show("Select a color", "info");
          return;
        }
        return;
      }
    }
    const newQuantity = quantity + delta;
    // Enforce max quantity limit only when limited_stock (product) or always (pack)
    const maxAllowedQuantity = isLimitedStock
      ? selectedVariant.quantity || 1
      : 99999;
    setQuantity(Math.max(1, Math.min(newQuantity, maxAllowedQuantity)));
  };

  const handleAddToCart = () => {
    if (!selectedVariant) {
      // If there's only one variant or all are onesize, auto-select it
      if (variants && variants.length === 1) {
        setSelectedVariant(variants[0]);
      } else if (
        variants &&
        variants.length > 0 &&
        displayableVariants.length === 0
      ) {
        // All variants are onesize - auto-select the first one
        const firstOnesize = variants.find((v) => isOnesize(v)) || variants[0];
        setSelectedVariant(firstOnesize);
      } else {
        // Check if size and color selection is required
        if (!selectedSize) {
          Toaster.show("Select a size", "info");
          return;
        }
        if (colorsForSelectedSize.length > 0 && !selectedColor) {
          Toaster.show("Select a color", "info");
          return;
        }
        return;
      }
    }
    // Calculate max quantity based on available quantity (only when limited)
    const maxAllowedQuantity = isLimitedStock
      ? selectedVariant.quantity || 1
      : null;

    // Catalog SKU (never size). Size/color are display aliases only.
    const catalogSku =
      selectedVariant?.variant_sku ||
      selectedVariant?.client_sku ||
      selectedVariant?.sku ||
      null;

    // Prepare cart item
    const cartItem = {
      sku: catalogSku,
      variant_sku: catalogSku,
      name: pack?.label || product?.label,
      image_url: getPackDisplayImage(pack, undefined, catalogSku),
      selling_price: pack?.pack_price || product?.price,
      selected_quantity: quantity,
      selected_size: selectedVariant?.size,
      selected_color: selectedVariant?.color ?? "",
      // Add IDs for shipment API
      pack_id: pack?.id,
      inventory_id: pack?.inventory_id,
      product_id: product?.product_id || product?.id,
      variant_id: selectedVariant.variant_id ?? selectedVariant.id,
      max_quantity: maxAllowedQuantity,
      limited_stock: isLimitedStock,
    };
    addToCart(cartItem);
    Toaster.show(added ? "Quantity Updated" : "Added to Cart", "success");
    setAdded(true);
  };

  const handleBuyNow = () => {
    if (!selectedVariant) {
      // If there's only one variant or all are onesize, auto-select it
      if (variants && variants.length === 1) {
        setSelectedVariant(variants[0]);
      } else if (
        variants &&
        variants.length > 0 &&
        displayableVariants.length === 0
      ) {
        // All variants are onesize - auto-select the first one
        const firstOnesize = variants.find((v) => isOnesize(v)) || variants[0];
        setSelectedVariant(firstOnesize);
      } else {
        // Check if size and color selection is required
        if (!selectedSize) {
          Toaster.show("Select a size", "info");
          return;
        }
        if (colorsForSelectedSize.length > 0 && !selectedColor) {
          Toaster.show("Select a color", "info");
          return;
        }
        return;
      }
    }
    // Calculate max quantity based on available quantity (only when limited)
    const maxAllowedQuantity = isLimitedStock
      ? selectedVariant.quantity || 1
      : null;

    // Catalog SKU (never size). Size/color are display aliases only.
    const catalogSku =
      selectedVariant?.variant_sku ||
      selectedVariant?.client_sku ||
      selectedVariant?.sku ||
      null;

    // Prepare cart item
    const cartItem = {
      sku: catalogSku,
      variant_sku: catalogSku,
      name: pack?.label || product?.label,
      image_url: getPackDisplayImage(pack, undefined, catalogSku),
      selling_price: pack?.pack_price || product?.price,
      selected_quantity: quantity,
      selected_size: selectedVariant?.size,
      selected_color: selectedVariant?.color ?? "",
      pack_id: pack?.id,
      inventory_id: pack?.inventory_id,
      product_id: product?.product_id || product?.id,
      variant_id: selectedVariant.variant_id ?? selectedVariant.id,
      max_quantity: maxAllowedQuantity,
      limited_stock: isLimitedStock,
    };
    addToCart(cartItem);
    Toaster.show("Added to Cart", "success");
    // Navigate to checkout immediately
    navigate("/checkout");
  };

  if (isLoading) return <Loader />;
  if (error) return <div>Error loading pack details.</div>;
  if (!pack) return <div>Pack not found.</div>;

  return (
    <>
      <Box
        sx={{
          backgroundColor: theme?.pageBackground,
          py: 3,
          flex: 1,
        }}
      >
        {product?.size_chart_url && showSizeChart ? (
          <Modal
            title="Size Chart"
            setShowSizeChart={setShowSizeChart}
            isOpen={true}
            imgUrl={product?.size_chart_url}
          />
        ) : null}

        <Container maxWidth="lg">
          <Grid container spacing={6}>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: "sticky",
                  top: 100,
                  backgroundColor: theme?.card_bg_color,
                  borderRadius: 2,
                  overflow: "hidden",
                  boxShadow: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "400px", // Ensure minimum height for centering
                }}
              >
                <ImageGallery
                  showPlayButton={false}
                  showFullscreenButton={false}
                  items={images}
                  showNav={images.length > 1}
                  showThumbnails={images.length > 1}
                  thumbnailPosition="bottom"
                  slideInterval={3000}
                  slideOnThumbnailOver={true}
                  infinite={false}
                  additionalClass="product-gallery"
                  style={{
                    width: "100%",
                    height: "100%",
                  }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  p: 3,
                  height: "fit-content",
                  boxShadow: "none",
                  border: `1px solid ${theme?.divider}`,
                  backgroundColor: theme?.card_bg_color,
                }}
              >
                <CardContent sx={{ p: 0 }}>
                  <AppTypography
                    variant="h4"
                    sx={{
                      fontWeight: 500,
                      color: theme?.app_text,
                      mb: 2,
                      fontSize: { xs: "1.5rem", md: "2rem" },
                    }}
                  >
                    {pack?.label}
                  </AppTypography>

                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 700,
                        color: theme?.product_details_price_color || "black",
                        fontSize: { xs: "1.4rem", md: "1.7rem" },
                      }}
                    >
                      {String.fromCodePoint(
                        storeInfo?.store?.currency_symbol || 0x20b9,
                      )}
                      {pack?.pack_price}
                    </Typography>
                  </Box>

                  {/* Onesize with multiple colors - show color picker only (no size selection) */}
                  {hasOnesizeColorVariants && hasColorPickerVariants ? (
                    <Box sx={{ mb: 3 }}>
                      <AppTypography
                        sx={{
                          fontWeight: 500,
                          fontSize: "0.9rem",
                          color: theme?.app_text,
                          mb: 1.5,
                        }}
                      >
                        {selectedColor ? "Color" : "Select Color *"}
                      </AppTypography>
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 1.5,
                          alignItems: "flex-start",
                        }}
                      >
                        {colorsForColorPicker.map((variant) => {
                          const hasColor =
                            variant.color != null &&
                            String(variant.color).trim() !== "";
                          const color = hasColor ? variant.color : null;
                          const selectionKey = color ?? `variant-${variant.id}`;
                          const isSelected =
                            selectedColor != null &&
                            (color != null
                              ? selectedColor?.toLowerCase() ===
                              color?.toLowerCase()
                              : selectedColor === selectionKey);
                          const isAvailable =
                            isUnlimitedProduct || variant.quantity > 0;

                          return (
                            <Box
                              key={variant.id}
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 0.5,
                                cursor: isAvailable ? "pointer" : "not-allowed",
                                opacity: isAvailable ? 1 : 0.5,
                              }}
                              onClick={() => {
                                if (isAvailable) {
                                  handleColorSelect(selectionKey);
                                }
                              }}
                            >
                              <ColorChoice
                                variant={variant}
                                isSelected={isSelected}
                                isAvailable={isAvailable}
                                theme={theme}
                              />
                              {!isAvailable && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: "0.65rem",
                                    color: theme?.app_text || "#666",
                                    opacity: 0.7,
                                    textAlign: "center",
                                    lineHeight: 1,
                                    mt: 0.25,
                                  }}
                                >
                                  Out of stock
                                </Typography>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  ) : null}

                  {/* Size/Color selection or fallback message */}
                  {hasVariants ? (
                    <Box sx={{ mb: 3 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 1,
                        }}
                      >
                        <AppTypography
                          sx={{
                            fontWeight: 500,
                            fontSize: "0.9rem",
                            color: theme?.app_text,
                          }}
                        >
                          {displayableVariants &&
                            displayableVariants.length === 1
                            ? "Size"
                            : "Select Size"}
                        </AppTypography>
                        {product?.size_chart_url ? (
                          <Button
                            variant="text"
                            onClick={() => setShowSizeChart(true)}
                            sx={{
                              color: theme?.product_details_price_color,
                              textDecoration: "underline",
                              p: 0,
                              minWidth: "auto",
                            }}
                          >
                            Size Chart
                          </Button>
                        ) : null}
                      </Box>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {uniqueSizes?.map((size) => {
                          const sizeVariants = variantsBySize[size] || [];
                          const hasAvailableStock =
                            isUnlimitedProduct ||
                            sizeVariants.some((v) => v.quantity > 0);
                          const button = (
                            <SizeButton
                              sx={{
                                backgroundColor:
                                  selectedSize === size
                                    ? theme?.app_button_color
                                    : "white",
                                color:
                                  selectedSize === size
                                    ? theme?.app_button_text_color || "black"
                                    : theme?.app_button_color,
                                border: `1px solid ${theme?.app_button_color}`,
                                "&:hover": {
                                  backgroundColor: theme?.app_button_color,
                                  color:
                                    theme?.app_button_text_color || "black",
                                  border: `1px solid ${theme?.app_button_color}`,
                                },
                              }}
                              key={size}
                              variant="outlined"
                              onClick={() => handleSizeSelect(size)}
                              selected={selectedSize === size}
                              disabled={!hasAvailableStock}
                            >
                              {size.toUpperCase()}
                            </SizeButton>
                          );
                          return !hasAvailableStock ? (
                            <Tooltip key={size} title="Out of stock" arrow>
                              <span>{button}</span>
                            </Tooltip>
                          ) : (
                            button
                          );
                        })}
                      </Box>
                      {/* Show available colors only when variants have color; size-only products work without color */}
                      {selectedSize &&
                        colorsForSelectedSize.length > 0 &&
                        hasColorVariants && (
                          <Box sx={{ mt: 2 }}>
                            <AppTypography
                              sx={{
                                fontWeight: 500,
                                fontSize: "0.9rem",
                                color: theme?.app_text,
                                mb: 1.5,
                              }}
                            >
                              {selectedColor ? "Color" : "Select Color *"}
                            </AppTypography>
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 1.5,
                                alignItems: "flex-start",
                              }}
                            >
                              {colorsForSelectedSize.map((variant) => {
                                const hasColor =
                                  variant.color != null &&
                                  String(variant.color).trim() !== "";
                                const color = hasColor ? variant.color : null;
                                const selectionKey =
                                  color ?? `variant-${variant.id}`;
                                const isSelected =
                                  selectedColor != null &&
                                  (color != null
                                    ? selectedColor?.toLowerCase() ===
                                    color?.toLowerCase()
                                    : selectedColor === selectionKey);
                                const isAvailable =
                                  isUnlimitedProduct || variant.quantity > 0;

                                return (
                                  <Box
                                    key={variant.id}
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      gap: 0.5,
                                      cursor: isAvailable
                                        ? "pointer"
                                        : "not-allowed",
                                      opacity: isAvailable ? 1 : 0.5,
                                    }}
                                    onClick={() => {
                                      if (isAvailable) {
                                        handleColorSelect(selectionKey);
                                      }
                                    }}
                                  >
                                    <ColorChoice
                                      variant={variant}
                                      isSelected={isSelected}
                                      isAvailable={isAvailable}
                                      theme={theme}
                                    />
                                    {!isAvailable && (
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          fontSize: "0.65rem",
                                          color: theme?.app_text || "#666",
                                          opacity: 0.7,
                                          textAlign: "center",
                                          lineHeight: 1,
                                          mt: 0.25,
                                        }}
                                      >
                                        Out of stock
                                      </Typography>
                                    )}
                                  </Box>
                                );
                              })}
                            </Box>
                          </Box>
                        )}
                    </Box>
                  ) : null}

                  {/* Only show out of stock if all variants are out of stock (skip for unlimited product) */}
                  {variants.length > 0 &&
                    !isUnlimitedProduct &&
                    variants.every((v) => v.quantity <= 0) && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        Out of stock
                      </Alert>
                    )}

                  {showQuantityAlert && isLimitedStock && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Only {maxQuantity} left
                    </Alert>
                  )}

                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="body2"
                      style={theme?.typography?.body}
                      sx={{ mb: 1 }}
                    >
                      Quantity
                    </Typography>
                    <Paper
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        borderRadius: 2,
                        p: 0.5,
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleQuantityChange(-1)}
                        disabled={
                          !selectedVariant ||
                          (!isUnlimitedProduct && selectedVariant.quantity <= 0)
                        }
                        sx={{
                          width: 36,
                          height: 36,
                          color: theme?.button_styles?.app?.backgroundColor,
                        }}
                      >
                        <RemoveRounded />
                      </IconButton>
                      <Typography
                        sx={{
                          mx: 2,
                          minWidth: 30,
                          textAlign: "center",
                          fontWeight: 600,
                          ...theme?.typography?.body,
                        }}
                      >
                        {quantity}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleQuantityChange(1)}
                        disabled={
                          !selectedVariant ||
                          (!isUnlimitedProduct &&
                            selectedVariant.quantity <= 0) ||
                          (isLimitedStock &&
                            quantity >= selectedVariant.quantity)
                        }
                        sx={{
                          width: 36,
                          height: 36,
                          color: theme?.button_styles?.app?.backgroundColor,
                        }}
                      >
                        <AddRounded />
                      </IconButton>
                    </Paper>
                    {/* Show available quantity for the selected variant (only when type is pack) */}
                    {selectedVariant && fetchType === "pack" && (
                      <Typography
                        variant="body2"
                        sx={{
                          display: "block",
                          mt: 2,
                          color: theme?.text_color,
                          opacity: 0.7,
                        }}
                      >
                        Available: {selectedVariant.quantity}
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="body1"
                      sx={{
                        ...theme?.typography?.body,
                        lineHeight: 1.6,
                      }}
                    >
                      {pack?.description || product?.description ? (
                        isHTML(pack?.description || product?.description) ? (
                          <span
                            dangerouslySetInnerHTML={{
                              __html: pack?.description || product?.description,
                            }}
                          />
                        ) : (
                          pack?.description || product?.description
                        )
                      ) : (
                        "No description available."
                      )}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      gap: { xs: 1, sm: 2 },
                      mb: 3,
                      flexWrap: "nowrap",
                    }}
                  >
                    <Button
                      variant="outlined"
                      disabled={
                        !selectedVariant ||
                        (!isUnlimitedProduct && selectedVariant.quantity <= 0)
                      }
                      onClick={handleAddToCart}
                      startIcon={<ShoppingCartOutlined />}
                      sx={{
                        flex: 1,
                        py: { xs: 1.25, sm: 1.5 },
                        fontSize: { xs: "0.85rem", sm: "1rem" },
                        fontWeight: 600,
                        borderRadius: 2,
                        borderColor: theme?.app_button_color,
                        color: theme?.app_button_color,
                        whiteSpace: "nowrap",
                        "&:hover": {
                          borderColor: theme?.app_button_color,
                          backgroundColor: theme?.app_button_color,
                          color: theme?.app_button_text_color,
                        },
                      }}
                    >
                      {added ? "Added to cart" : "Add to cart"}
                    </Button>
                    <Button
                      variant="contained"
                      disabled={
                        !selectedVariant ||
                        (!isUnlimitedProduct && selectedVariant.quantity <= 0)
                      }
                      onClick={handleBuyNow}
                      startIcon={<LocalMallOutlined />}
                      sx={{
                        flex: 1,
                        py: { xs: 1.25, sm: 1.5 },
                        fontSize: { xs: "0.85rem", sm: "1rem" },
                        fontWeight: 600,
                        borderRadius: 2,
                        backgroundColor: theme?.app_button_color,
                        color: theme?.app_button_text_color,
                        whiteSpace: "nowrap",
                        "&:hover": {
                          backgroundColor: theme?.app_hover_button_color,
                          opacity: 0.9,
                        },
                      }}
                    >
                      Buy Now
                    </Button>
                  </Box>

                  <Divider sx={{ my: 2 }} />
                  <Box>
                    <AppTypography
                      variant="h6"
                      sx={{
                        mb: 1,
                        fontWeight: 600,
                        color: theme?.app_text,
                        fontSize: "1rem",
                      }}
                    >
                      Product Policies
                    </AppTypography>
                    <Stack spacing={1}>
                      <AppTypography
                        variant="body2"
                        sx={{
                          fontSize: "0.8rem",
                          fontWeight: 500,
                          color: theme?.app_text,
                        }}
                      >
                        • Pay on delivery not available
                      </AppTypography>
                      <AppTypography
                        variant="body2"
                        sx={{
                          fontSize: "0.8rem",
                          fontWeight: 500,
                          color: theme?.app_text,
                        }}
                      >
                        • No returns or exchange
                      </AppTypography>
                      <AppTypography
                        variant="body2"
                        sx={{
                          fontSize: "0.8rem",
                          fontWeight: 500,
                          color: theme?.app_text,
                        }}
                      >
                        • Price is All Inclusive( Branding, Delivery & GST)
                      </AppTypography>
                    </Stack>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default ProductDetails;
